import React, { useContext } from 'react';
import { graphql, StaticQuery } from 'gatsby';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { space } from 'styled-system';
import { get } from 'lodash';
import BackgroundImage from 'gatsby-background-image';
import Container from '@firsttable/web-components/atoms/Container/Container';
import {
  Title,
  Box,
  Row,
  Col,
  Divider,
  Link,
  Flex,
  Icon,
  BodyTitle,
  Text,
} from '@firsttable/web-components/atoms';
import { themeGet } from '@styled-system/theme-get';
import { useLocation } from '@reach/router';
import queryString from 'query-string';
import HomeSignInForm from '../Forms/HomeSignInForm';
import MainMenu from '../Navigation/Menu';
import AuthContext from '../../../context/AuthContext';
import ModalContext from '../../../context/ModalContext';
import SignUp from '../Auth/SignUp';
import parseHtml from '../../../helpers/parseHtml';

const StyledImageBackground = ({ className, children }) => (
  <StaticQuery
    query={graphql`
      query {
        cityDesktop: file(relativePath: { eq: "home-bgv3.jpg" }) {
          childImageSharp {
            fluid(quality: 60, maxWidth: 1160, cropFocus: NORTH) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
      }
    `}
    render={(data) => {
      // Set ImageData.
      const imageData = data.cityDesktop.childImageSharp.fluid;
      return (
        <BackgroundImage
          Tag="section"
          className={className}
          fluid={imageData}
          style={{ overflow: null }}
        >
          {children}
        </BackgroundImage>
      );
    }}
  />
);
StyledImageBackground.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const StyledBackgroundSection = styled(StyledImageBackground)`
  background: linear-gradient(
    0deg,
    rgba(0, 0, 0, 0.1),
    rgba(0, 0, 0, 0.6),
    rgba(0, 0, 0, 0.5)
  );
  mix-blend-mode: normal;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: 50% 50%;
  position: relative;
  opacity: 1 !important;
  ${space};
`;

const SignUpBox = styled(Box)`
  background-image: linear-gradient(
    180deg,
    ${themeGet('colors.grayscale.300')} 30%,
    #fff 100%
  );
  border-radius: 2px;
`;

const HeroCurve = styled(Box)`
  pointer-events: none;
  position: absolute;
  bottom: -1px;
  left: 0;
  height: 113px;
  width: 100%;
  overflow: hidden;
  display: flex;
  align-items: flex-end;

  @media (min-width: 1600px) {
    height: 100%;
  }
`;

const PromoMessage = styled(Box)`
  a {
    color: ${themeGet('colors.white')};
    border-bottom: 1px solid;
    &:hover {
      border-bottom-color: transparent;
    }
  }
`;

const HomeHero = ({ regions, city, ...props }) => {
  const { isLoggedIn } = useContext(AuthContext);
  const isUserLoggedIn = isLoggedIn();
  const { search } = useLocation();
  const { showModalFunc } = useContext(ModalContext);
  const [comingSoonPromo] = city?.blocks
    ? city.blocks.edges.filter(({ node }) => node.key === 'ComingSoonBanner')
    : [null];

  return (
    <StyledBackgroundSection mb={['50px', '90px']}>
      <MainMenu transparent />
      <Container pt={30} pb={['2%', null, '3.5%']}>
        <Row alignItems="center">
          <Col width={[1, null, 1 / 2]} zIndex={1}>
            {!comingSoonPromo && (
              <Title
                textAlign={['center', null, 'left']}
                mb={['l', 32]}
                size="h1"
                color="white"
                maxWidth={[null, null, '546px']}
              >
                The early bird gets the dinner deal
              </Title>
            )}
            <Text textAlign={['center', null, 'left']} color="white">
              Book a First Table at our partner restaurants and get 50% off the{' '}
              food bill for two to four people. Talk about the early bird
              catches the worm!
            </Text>
            {comingSoonPromo?.node && (
              <Box
                m="auto"
                mb={[50, 50, 0]}
                maxWidth={['100%', 454, 650]}
                color="white"
              >
                <Flex
                  alignItems="center"
                  mb="30px"
                  display={['none', null, 'flex']}
                >
                  <Icon
                    width="1em"
                    height="1.2em"
                    viewBox="0 0 40 48"
                    fontSize="34px"
                    name="cutlery"
                    color="white"
                  />
                  <Box ml="30px" height="1px" bg="white" width="174px" />
                </Flex>
                <Title
                  color={null}
                  as="h1"
                  fontSize={['heading.h4', 'heading.h3', 'heading.hero']}
                  textAlign={['center', 'center', 'left']}
                >
                  {comingSoonPromo?.node?.title
                    ? comingSoonPromo.node.title
                    : city.title}
                </Title>
                <PromoMessage color="white">
                  {parseHtml(comingSoonPromo.node.content)}
                </PromoMessage>
              </Box>
            )}
          </Col>
          <Col width={[1, null, 1 / 2]}>
            <SignUpBox
              p={
                isUserLoggedIn
                  ? ['32px 32px 60px', '54px 77px 77px', '100px 77px 100px']
                  : ['32px 32px 48px', '54px 77px', '54px 77px 77px']
              }
              width={['100%', '454px']}
              mx="auto"
              mb={[-1, 0, 0]}
              minHeight={550}
            >
              {!get(queryString.parse(search), 'confirmed') ? (
                <>
                  <HomeSignInForm
                    regions={regions}
                    cityId={city?.foreignId}
                    isUserLoggedIn={isUserLoggedIn}
                    city={city}
                    {...props}
                  />
                  {!isUserLoggedIn && (
                    <>
                      <Divider m="35px 0 28px" separatorText="Or" />
                      <Box textAlign="center">
                        <Link
                          onClick={() =>
                            showModalFunc(
                              <SignUp isModal cityId={city?.foreignId} />,
                              { type: 'signUp' },
                            )
                          }
                          color="brand.red"
                          underline
                        >
                          Continue with email
                        </Link>
                      </Box>
                    </>
                  )}
                </>
              ) : (
                <>
                  <Box textAlign="center" mb="13px">
                    <Icon
                      name="logo-bird"
                      color="brand.blue"
                      width={isUserLoggedIn ? 50 : 40}
                      height={isUserLoggedIn ? 50 : 40}
                      viewBox="0 0 29 30"
                    />
                  </Box>
                  <BodyTitle size="l" mt={0} mb="8px" textAlign="center">
                    Thank you
                  </BodyTitle>
                  <span>
                    <Text mb="10px" textAlign="center">
                      Thanks for signing up. We’ll add the credit to your
                      account once we’re live.
                    </Text>
                  </span>
                </>
              )}
            </SignUpBox>
          </Col>
        </Row>
      </Container>
      <HeroCurve>
        <svg
          width="100%"
          viewBox="0 0 1600 113"
          fill="none"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M0 53.6142V112.999H1600V4.56963C1600 4.56963 1336.46 -20.4525 913.459 46.6083C490.459 113.669 0 53.6142 0 53.6142Z"
            fill="white"
          />
        </svg>
      </HeroCurve>
    </StyledBackgroundSection>
  );
};

HomeHero.propTypes = {
  regions: PropTypes.object,
  city: PropTypes.shape(),
};
HomeHero.defaultProps = {
  regions: {},
  city: null,
};

export default HomeHero;
